import { converter, formatHex, formatCss, parse, wcagContrast, type Oklch } from 'culori';

/**
 * Color utilities for the multi-tenant branding pipeline.
 *
 * The previous Angular implementation generated palette shades by lerping
 * RGB toward white/black, which produces muddy mid-tones on saturated brand
 * colors. This OKLCH-based version keeps perceptual lightness uniform across
 * the palette by manipulating the L axis only — Chroma and Hue are preserved
 * (with a gentle desaturation at the extremes for legibility).
 */

const toOklch = converter('oklch');

export interface PrimaryShades {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
}

const FALLBACK_SHADES: PrimaryShades = {
  50:  'oklch(97% 0.012 270)',
  100: 'oklch(93% 0.020 270)',
  200: 'oklch(86% 0.032 270)',
  300: 'oklch(75% 0.046 270)',
  400: 'oklch(60% 0.060 270)',
  500: 'oklch(48% 0.070 270)',
  600: 'oklch(38% 0.060 270)',
  700: 'oklch(30% 0.050 270)',
  800: 'oklch(22% 0.040 270)',
  900: 'oklch(15% 0.030 270)',
};

/**
 * Lightness targets for each palette stop. 600 = the brand input (preserved).
 * Values chosen so contrast steps feel even — 50→100 is shorter than
 * 800→900 because eye-perception flattens at the lightness extremes.
 */
const SHADE_LIGHTNESS: Record<keyof PrimaryShades, number> = {
  50:  0.97,
  100: 0.94,
  200: 0.88,
  300: 0.78,
  400: 0.66,
  500: 0.55,
  600: 0.46, // base — overridden below to match input lightness exactly
  700: 0.38,
  800: 0.30,
  900: 0.22,
};

/**
 * Chroma scaling per stop. Pulling chroma slightly toward zero at the
 * lightness extremes prevents neon-looking 50/100 and washed-out 800/900.
 */
const CHROMA_SCALE: Record<keyof PrimaryShades, number> = {
  50:  0.30,
  100: 0.45,
  200: 0.65,
  300: 0.85,
  400: 0.95,
  500: 1.00,
  600: 1.00,
  700: 1.00,
  800: 0.90,
  900: 0.80,
};

/**
 * Generate a 10-stop palette from a brand primary color (any CSS color string).
 * Returns OKLCH strings — assign via `--primary-NNN` CSS vars.
 */
export function generatePrimaryShades(input: string): PrimaryShades {
  const parsed = parse(input);
  if (!parsed) {
    console.warn(`[color] Could not parse primary "${input}", using fallback`);
    return FALLBACK_SHADES;
  }

  const base = toOklch(parsed) as Oklch | undefined;
  if (!base || base.l == null) {
    console.warn(`[color] Could not convert "${input}" to OKLCH, using fallback`);
    return FALLBACK_SHADES;
  }

  const baseChroma = base.c ?? 0;
  const hue = base.h ?? 270;
  const baseLightness = base.l;

  const stop = (key: keyof PrimaryShades): string => {
    const targetL = key === 600 ? baseLightness : SHADE_LIGHTNESS[key];
    const targetC = baseChroma * CHROMA_SCALE[key];
    return formatCss({ mode: 'oklch', l: targetL, c: targetC, h: hue });
  };

  return {
    50:  stop(50),
    100: stop(100),
    200: stop(200),
    300: stop(300),
    400: stop(400),
    500: stop(500),
    600: stop(600),
    700: stop(700),
    800: stop(800),
    900: stop(900),
  };
}

/**
 * Pick a foreground ink color (white-ish or near-black) that meets WCAG AA
 * contrast against the given background. Used for text rendered inside
 * `--primary-600` blocks (badge labels, button text on solid primary).
 */
export function pickInkOnPrimary(input: string): string {
  const parsed = parse(input);
  const oklch = parsed ? (toOklch(parsed) as Oklch | undefined) : undefined;
  if (!oklch || oklch.l == null) return 'oklch(99% 0 0)';
  return oklch.l > 0.62 ? 'oklch(15% 0.012 270)' : 'oklch(99% 0 0)';
}

/** Text colours on a surface (tokens.css › "Text on custom surfaces"), by the surface's tone. */
const LIGHT_INK = {
  'ink-primary': 'oklch(98% 0.004 92)',
  'ink-secondary': 'oklch(88% 0.006 92)',
  'ink-muted': 'oklch(78% 0.006 92)',
  'ink-disabled': 'oklch(64% 0.006 92)',
  hairline: 'oklch(100% 0 0 / 0.16)',
};
const DEEP_INK = {
  'ink-primary': 'oklch(16% 0.012 145)',
  'ink-secondary': 'oklch(28% 0.012 145)',
  'ink-muted': 'oklch(36% 0.010 130)',
  'ink-disabled': 'oklch(48% 0.008 110)',
  hairline: 'oklch(0% 0 0 / 0.14)',
};

/** Every token surfaceTokens can set, so a style change can clear the last one's first. */
export const SURFACE_TOKENS = ['paper', 'card'].flatMap((surface) => [
  ...Object.keys(LIGHT_INK).map((key) => `--${surface}-${key}`),
  `--${surface}-link`,
  `--${surface}-tint-50`,
  `--${surface}-tint-100`,
  `--${surface}-scheme`,
]).concat('--white-link', '--surface-paper', '--surface-card', '--surface-elevated', '--surface-sunken');

function isDark(color: string): boolean {
  return wcagContrast(color, '#ffffff') > wcagContrast(color, '#1b1d18');
}

/**
 * The shade primary text and icons take on a surface, or null to keep each utility's own shade.
 * Those shades are dark, so dark surfaces always swap to a light one; light surfaces only when
 * the primary itself would fade on them.
 */
function linkOn(surface: string, primary: string): string | null {
  if (isDark(surface)) return 'var(--primary-200)';
  return wcagContrast(primary, surface) < 3 ? 'var(--primary-800)' : null;
}

/**
 * The tokens for one custom surface colour: the surface itself, its text colours and its link shade.
 * Dark surfaces take light text; mid-tone light ones darker text than the built-in greys, which would
 * fade on them; near-white ones keep the built-in text.
 */
export function surfaceTokens(surface: 'paper' | 'card', color: string, primary: string): Record<string, string> {
  const oklch = toOklch(parse(color) ?? '') as Oklch | undefined;
  if (!oklch || oklch.l == null) return {};
  const tokens: Record<string, string> = { [`--surface-${surface}`]: color };

  // 1. Text colours by tone.
  const ink = isDark(color) ? LIGHT_INK : oklch.l < 0.9 ? DEEP_INK : null;
  for (const [key, value] of Object.entries(ink ?? {})) tokens[`--${surface}-${key}`] = value;

  // 2. Primary text, when the primary alone would fade on it.
  const link = linkOn(color, primary);
  if (link) tokens[`--${surface}-link`] = link;

  // 3. Pale primary tints would glare on a dark surface, so they become a dim wash of the primary.
  //    Native controls (select lists, date and time pickers, scrollbars) switch to their dark look too.
  if (isDark(color)) {
    tokens[`--${surface}-scheme`] = 'dark';
    tokens[`--${surface}-tint-50`] = `color-mix(in oklch, ${color}, var(--primary-500) 14%)`;
    tokens[`--${surface}-tint-100`] = `color-mix(in oklch, ${color}, var(--primary-500) 24%)`;
  }

  // 4. Cards bring their raised and sunken neighbours along, a step lighter and darker.
  if (surface === 'card') {
    tokens['--surface-elevated'] = isDark(color) ? `color-mix(in oklch, ${color}, white 6%)` : color;
    if (isDark(color)) tokens['--surface-sunken'] = `color-mix(in oklch, ${color}, black 25%)`;
  }
  return tokens;
}

/** The link shade for plain white surfaces, which keep their colour whatever the style. */
export function whiteLink(primary: string): string | null {
  return linkOn('#ffffff', primary);
}

/**
 * Apply primary shades + ink color to an element's inline style. Used by
 * brandingStore to mutate `:root` (global tenant) or a scoped element
 * (embedded onboarding inside a host with different branding).
 */
export function applyPrimaryShadesToElement(
  el: HTMLElement,
  primaryColor: string,
): void {
  const shades = generatePrimaryShades(primaryColor);
  el.style.setProperty('--primary-50', shades[50]);
  el.style.setProperty('--primary-100', shades[100]);
  el.style.setProperty('--primary-200', shades[200]);
  el.style.setProperty('--primary-300', shades[300]);
  el.style.setProperty('--primary-400', shades[400]);
  el.style.setProperty('--primary-500', shades[500]);
  el.style.setProperty('--primary-600', shades[600]);
  el.style.setProperty('--primary-700', shades[700]);
  el.style.setProperty('--primary-800', shades[800]);
  el.style.setProperty('--primary-900', shades[900]);
  el.style.setProperty('--primary-ink', pickInkOnPrimary(primaryColor));
}

/** Remove primary shade overrides applied via {@link applyPrimaryShadesToElement}. */
export function clearPrimaryShadesFromElement(el: HTMLElement): void {
  for (const key of [
    '--primary-50', '--primary-100', '--primary-200', '--primary-300', '--primary-400',
    '--primary-500', '--primary-600', '--primary-700', '--primary-800', '--primary-900',
    '--primary-ink',
  ]) {
    el.style.removeProperty(key);
  }
}

/**
 * Apply a single brand accent color (e.g. the brass / tertiary accents)
 * plus a contrast-safe ink, as `colorVar` / `inkVar` CSS custom properties.
 * Used by brandingStore so the accents are tenant-branded rather than
 * hardcoded. No-op when the color can't be parsed (keeps the token default).
 */
export function applyAccentColorToElement(
  el: HTMLElement,
  colorVar: string,
  inkVar: string,
  color: string,
): void {
  const parsed = parse(color);
  if (!parsed) return;
  const oklch = toOklch(parsed) as Oklch | undefined;
  if (!oklch || oklch.l == null) return;
  el.style.setProperty(
    colorVar,
    formatCss({ mode: 'oklch', l: oklch.l, c: oklch.c ?? 0, h: oklch.h ?? 0 }),
  );
  el.style.setProperty(inkVar, oklch.l > 0.62 ? 'oklch(20% 0.030 150)' : 'oklch(99% 0 0)');
}

/** Remove accent overrides applied via {@link applyAccentColorToElement}. */
export function clearAccentColorsFromElement(el: HTMLElement): void {
  for (const key of [
    '--accent-brass', '--accent-brass-ink',
    '--accent-tertiary', '--accent-tertiary-ink',
  ]) {
    el.style.removeProperty(key);
  }
}

/**
 * Convert any CSS color string to a `#rrggbb` hex. Used when a host system
 * (e.g. Capacitor StatusBar) only accepts hex.
 */
export function toHex(input: string): string | undefined {
  const parsed = parse(input);
  return parsed ? formatHex(parsed) : undefined;
}
